import type {
  DiagnosticProfile,
  TreatmentType,
} from "@src/packages/homepage/utils/costEstimator";
import {
  getMedicationLineId,
  resolveEstimateAmount,
  roundModeledEstimate,
} from "./estimator";
import type { EstimateLocationInput } from "./estimator";
import type { EstimateLineId } from "./catalog";

export type EstimateLineStatus =
  | "likely-included"
  | "often-separate"
  | "optional";

export type EstimateLineMultiplier =
  | "once"
  | "cycle"
  | "embryo"
  | "storage-year"
  | "transfer";

export type EstimateScenarioLine = {
  key: string;
  label: string;
  amount: number;
  status: EstimateLineStatus;
  multiplier: EstimateLineMultiplier;
  defaultSelected: boolean;
  detail: string;
};

export type EstimateScenarioSection = {
  key: string;
  title: string;
  description: string;
  lines: EstimateScenarioLine[];
};

export type EstimateScenarioInput = {
  treatment: TreatmentType;
  diagnosticProfile: DiagnosticProfile;
  medicationIntensity: "low" | "typical" | "high";
  estimateLocation: EstimateLocationInput;
};

function getMedicationFactor(intensity: "low" | "typical" | "high") {
  if (intensity === "low") return 0.72;
  if (intensity === "high") return 1.45;
  return 1;
}

export function isRetrievalTreatment(treatment: TreatmentType) {
  return (
    treatment === "egg_freezing" ||
    treatment === "embryo_freezing" ||
    treatment === "ivf" ||
    treatment === "ivf_icsi"
  );
}

export function hasEmbryoLabPath(treatment: TreatmentType) {
  return (
    treatment === "embryo_freezing" ||
    treatment === "ivf" ||
    treatment === "ivf_icsi"
  );
}

export function hasTransferPath(treatment: TreatmentType) {
  return treatment === "ivf" || treatment === "ivf_icsi" || treatment === "fet";
}

export function hasDonorOptionsPath(treatment: TreatmentType) {
  return (
    treatment === "embryo_freezing" ||
    treatment === "ivf" ||
    treatment === "ivf_icsi" ||
    treatment === "fet" ||
    treatment === "iui"
  );
}

export function buildEstimateScenarioSections({
  treatment,
  diagnosticProfile,
  medicationIntensity,
  estimateLocation,
}: EstimateScenarioInput): EstimateScenarioSection[] {
  const amount = (lineId: EstimateLineId) =>
    roundModeledEstimate(resolveEstimateAmount(lineId, estimateLocation));
  const sections: EstimateScenarioSection[] = [];
  const beforeCycleLines: EstimateScenarioLine[] = [
    {
      key: "consult",
      label: "Consultation and financial review",
      amount: amount("consult"),
      status: "often-separate",
      multiplier: "once",
      defaultSelected: true,
      detail:
        "Cost changes if the clinic waives the consult, credits it to treatment, or requires a separate financial visit.",
    },
  ];

  if (diagnosticProfile === "female" || diagnosticProfile === "couple") {
    beforeCycleLines.push({
      key: "female-workup",
      label: "Hormones, AMH, and baseline ultrasound",
      amount: amount("female-workup"),
      status: "often-separate",
      multiplier: "once",
      defaultSelected: true,
      detail:
        "Cost changes with which labs are ordered, where bloodwork is drawn, and whether ultrasound is billed by the clinic.",
    });
  }

  if (diagnosticProfile === "male" || diagnosticProfile === "couple") {
    beforeCycleLines.push({
      key: "male-workup",
      label: "Semen analysis and partner labs",
      amount: amount("male-workup"),
      status: "often-separate",
      multiplier: "once",
      defaultSelected:
        treatment === "embryo_freezing" ||
        treatment === "ivf" ||
        treatment === "ivf_icsi" ||
        treatment === "iui",
      detail:
        "Cost changes with semen analysis type, infectious-disease labs, partner insurance, and whether labs bill separately.",
    });
  }

  if (treatment !== "egg_freezing" && treatment !== "sperm_freezing") {
    beforeCycleLines.push({
      key: "uterine-cavity",
      label: "HSG, SIS, or HyCoSy cavity check",
      amount: amount("uterine-cavity"),
      status: "optional",
      multiplier: "once",
      defaultSelected: treatment === "ivf" || treatment === "ivf_icsi",
      detail:
        "Cost changes by test type, imaging center, contrast, radiology read, and facility fee.",
    });
  }

  sections.push({
    key: "before-cycle",
    title: "Before cycle",
    description: "Testing and planning costs that can appear before treatment.",
    lines: beforeCycleLines,
  });

  const cycleLines: EstimateScenarioLine[] = [];
  if (treatment === "egg_freezing") {
    cycleLines.push({
      key: "egg-freezing-cycle",
      label: "Egg freezing cycle package",
      amount: amount("egg-freezing-cycle"),
      status: "likely-included",
      multiplier: "cycle",
      defaultSelected: true,
      detail:
        "Ask whether medications, anesthesia, storage, monitoring visits, and outside labs are separate.",
    });
  } else if (treatment === "embryo_freezing") {
    cycleLines.push({
      key: "embryo-freezing-cycle",
      label: "Embryo freezing cycle package",
      amount: amount("embryo-freezing-cycle"),
      status: "likely-included",
      multiplier: "cycle",
      defaultSelected: true,
      detail:
        "Ask whether fertilization, culture length, freezing, anesthesia, storage, and lab add-ons are separate.",
    });
  } else if (treatment === "ivf" || treatment === "ivf_icsi") {
    cycleLines.push({
      key: "ivf-cycle",
      label: "IVF retrieval and lab cycle",
      amount: amount("ivf-cycle"),
      status: "likely-included",
      multiplier: "cycle",
      defaultSelected: true,
      detail:
        "Ask whether medications, anesthesia, embryo freezing, storage, genetic testing, and transfer are separate.",
    });
  } else if (treatment === "fet") {
    cycleLines.push({
      key: "fet-cycle",
      label: "Frozen embryo transfer cycle",
      amount: amount("fet-cycle"),
      status: "likely-included",
      multiplier: "transfer",
      defaultSelected: true,
      detail:
        "Ask whether monitoring, thaw, embryo handling, transfer meds, and outside labs are separate.",
    });
  } else if (treatment === "iui") {
    cycleLines.push({
      key: "iui-cycle",
      label: "Monitored IUI cycle",
      amount: amount("iui-cycle"),
      status: "likely-included",
      multiplier: "cycle",
      defaultSelected: true,
      detail:
        "Ask whether monitoring, bloodwork, trigger shot, sperm prep, and donor sperm are separate.",
    });
  } else if (treatment === "ovulation_induction") {
    cycleLines.push({
      key: "oi-cycle",
      label: "Ovulation induction monitoring",
      amount: amount("oi-cycle"),
      status: "likely-included",
      multiplier: "cycle",
      defaultSelected: true,
      detail:
        "Cost changes with monitoring visit count, bloodwork frequency, trigger medication, and pharmacy billing.",
    });
  } else if (treatment === "sperm_freezing") {
    cycleLines.push({
      key: "sperm-freezing-cycle",
      label: "Sperm freezing intake and cryopreservation",
      amount: amount("sperm-freezing-cycle"),
      status: "likely-included",
      multiplier: "cycle",
      defaultSelected: true,
      detail:
        "Ask whether analysis, infectious labs, freezing, storage, and future shipping are separate.",
    });
  }

  if (isRetrievalTreatment(treatment)) {
    cycleLines.push({
      key: "anesthesia",
      label: "Retrieval anesthesia",
      amount: amount("anesthesia"),
      status: "often-separate",
      multiplier: "cycle",
      defaultSelected: true,
      detail:
        "Cost changes if anesthesia bills through a separate group, facility, or insurance claim.",
    });
  }

  sections.push({
    key: "cycle",
    title: "Cycle and procedure",
    description: "The main clinical package for the selected treatment.",
    lines: cycleLines,
  });

  const medicationLineId = getMedicationLineId(treatment);
  const medicationBase = medicationLineId ? amount(medicationLineId) : 0;
  if (medicationLineId != null) {
    sections.push({
      key: "medications",
      title: "Medications",
      description:
        "Your protocol, pharmacy, insurance, and savings programs can move this number a lot.",
      lines: [
        {
          key: "medications",
          label:
            treatment === "fet"
              ? "Transfer preparation medications"
              : treatment === "iui"
                ? "IUI medications"
                : treatment === "ovulation_induction"
                  ? "Ovulation induction medications"
                  : "Stimulation medications",
          amount: roundModeledEstimate(
            medicationBase * getMedicationFactor(medicationIntensity),
          ),
          status: "often-separate",
          multiplier: treatment === "fet" ? "transfer" : "cycle",
          defaultSelected: true,
          detail:
            medicationIntensity === "high"
              ? "Higher-dose plans can need more FSH/LH, antagonist, trigger, and refill orders."
              : medicationIntensity === "low"
                ? "Lower-intensity plans may use fewer injections, but brand, pharmacy, and discounts still matter."
                : "Cost changes with dose, brand, pharmacy, insurance coverage, cash pricing, and savings-program eligibility.",
        },
      ],
    });
  }

  const labLines: EstimateScenarioLine[] = [];
  if (hasEmbryoLabPath(treatment)) {
    labLines.push({
      key: "icsi",
      label: "ICSI fertilization",
      amount: amount("icsi"),
      status: treatment === "ivf_icsi" ? "likely-included" : "optional",
      multiplier: "cycle",
      defaultSelected: treatment === "ivf_icsi",
      detail:
        "Cost changes if ICSI is priced per cycle, per egg, or already bundled into the IVF package.",
    });
    labLines.push({
      key: "pgta",
      label: "PGT-A biopsy, shipping, and lab testing",
      amount: amount("pgta"),
      status: "optional",
      multiplier: "embryo",
      defaultSelected: false,
      detail:
        "Cost changes with embryo count, biopsy fee, shipping, genetic lab pricing, and counseling.",
    });
    labLines.push({
      key: "pgtm-sr",
      label: "PGT-M or PGT-SR setup and testing",
      amount: amount("pgtm-sr"),
      status: "optional",
      multiplier: "once",
      defaultSelected: false,
      detail:
        "Used for a known inherited condition or chromosome rearrangement; cost changes with test setup and family samples.",
    });
    labLines.push({
      key: "pgtp",
      label: "PGT-P polygenic risk screening",
      amount: amount("pgtp"),
      status: "optional",
      multiplier: "embryo",
      defaultSelected: false,
      detail:
        "Not offered by every clinic or lab; cost changes with embryo count and the specific screening panel.",
    });
    labLines.push({
      key: "assisted-hatching",
      label: "Assisted hatching or embryo prep add-on",
      amount: amount("assisted-hatching"),
      status: "optional",
      multiplier: "cycle",
      defaultSelected: false,
      detail:
        "Cost changes if the clinic recommends it selectively, bundles it, or prices embryo prep separately.",
    });
  }

  if (treatment === "iui") {
    labLines.push({
      key: "sperm-wash",
      label: "Sperm wash/preparation",
      amount: amount("sperm-wash"),
      status: "often-separate",
      multiplier: "cycle",
      defaultSelected: true,
      detail:
        "Cost changes with wash method, sperm source, same-day lab timing, and andrology billing.",
    });
  }

  if (labLines.length > 0) {
    sections.push({
      key: "lab-add-ons",
      title: "Lab add-ons",
      description: "Embryology or sperm-lab services that vary by treatment.",
      lines: labLines,
    });
  }

  if (hasDonorOptionsPath(treatment)) {
    const donorLines: EstimateScenarioLine[] = [];

    if (
      treatment === "iui" ||
      treatment === "embryo_freezing" ||
      treatment === "ivf" ||
      treatment === "ivf_icsi"
    ) {
      donorLines.push({
        key: "donor-sperm",
        label: "Donor sperm vial, shipping, and handling",
        amount: amount("donor-sperm"),
        status: "optional",
        multiplier: treatment === "iui" ? "cycle" : "once",
        defaultSelected: false,
        detail:
          "Cost changes with sperm bank, vial count, donor type, genetic screening, shipping, and storage.",
      });
    }

    if (
      treatment === "embryo_freezing" ||
      treatment === "ivf" ||
      treatment === "ivf_icsi"
    ) {
      donorLines.push({
        key: "donor-eggs",
        label: "Donor eggs or donor egg cycle",
        amount: amount("donor-eggs"),
        status: "optional",
        multiplier: "once",
        defaultSelected: false,
        detail:
          "Cost changes with frozen eggs vs fresh donor, egg bank fees, donor compensation, agency fees, and legal work.",
      });
    }

    if (treatment === "fet") {
      donorLines.push({
        key: "donor-embryo",
        label: "Donor embryo coordination",
        amount: amount("donor-embryo"),
        status: "optional",
        multiplier: "once",
        defaultSelected: false,
        detail:
          "Cost changes with matching program, legal review, embryo shipping, receiving-clinic fees, and required screening.",
      });
    }

    donorLines.push({
      key: "known-donor-coordination",
      label: "Known donor legal and screening",
      amount: amount("known-donor-coordination"),
      status: "optional",
      multiplier: "once",
      defaultSelected: false,
      detail:
        "Cost changes with attorney requirements, donor testing, psych consults, FDA screening, and clinic coordination.",
    });

    sections.push({
      key: "donor-options",
      title: "Donor options",
      description: "Sperm, eggs, embryos, and known-donor steps if they apply.",
      lines: donorLines,
    });
  }

  const storageLines: EstimateScenarioLine[] = [];
  if (
    treatment === "egg_freezing" ||
    treatment === "embryo_freezing" ||
    treatment === "ivf" ||
    treatment === "ivf_icsi" ||
    treatment === "sperm_freezing"
  ) {
    storageLines.push({
      key: "storage",
      label:
        treatment === "sperm_freezing"
          ? "Sperm storage"
          : treatment === "egg_freezing"
            ? "Egg storage"
            : "Embryo storage",
      amount: amount("storage"),
      status: "often-separate",
      multiplier: "storage-year",
      defaultSelected: true,
      detail:
        "Cost changes with first-year inclusion, specimen count, storage vendor, and annual renewal price.",
    });
    storageLines.push({
      key: "specimen-transfer",
      label: "Future specimen transfer or shipping",
      amount: amount("specimen-transfer"),
      status: "optional",
      multiplier: "once",
      defaultSelected: false,
      detail:
        "Cost changes with transfer-out paperwork, courier type, distance, and receiving-clinic fees.",
    });
  }

  if (storageLines.length > 0) {
    sections.push({
      key: "storage",
      title: "Storage",
      description: "Recurring cryostorage and future transport considerations.",
      lines: storageLines,
    });
  }

  if (hasTransferPath(treatment)) {
    sections.push({
      key: "transfer-path",
      title: "Transfer path",
      description: "Costs after embryos exist and a transfer is planned.",
      lines: [
        {
          key: "additional-fet",
          label:
            treatment === "fet"
              ? "Additional transfer cycle"
              : "Frozen transfer",
          amount: amount("additional-fet"),
          status: treatment === "fet" ? "optional" : "often-separate",
          multiplier: "transfer",
          defaultSelected: treatment !== "fet",
          detail:
            "Cost changes if transfer, thaw, monitoring, embryo handling, and procedure fee are billed separately.",
        },
        {
          key: "transfer-meds",
          label: "Transfer medications",
          amount: amount("transfer-meds"),
          status: "often-separate",
          multiplier: "transfer",
          defaultSelected: treatment !== "fet",
          detail:
            "Cost changes with progesterone route, estrogen plan, cycle length, pharmacy, and insurance coverage.",
        },
      ],
    });
  }

  sections.push({
    key: "future-scenarios",
    title: "Future scenarios",
    description: "Not always part of today's bill, but useful for budgeting.",
    lines: [
      {
        key: "extra-cycle-buffer",
        label:
          treatment === "egg_freezing"
            ? "Possible additional freezing round"
            : treatment === "iui" || treatment === "ovulation_induction"
              ? "Possible additional cycle"
              : "Possible additional retrieval cycle",
        amount:
          treatment === "egg_freezing"
            ? amount("egg-freezing-cycle") + medicationBase
            : treatment === "iui"
              ? amount("iui-cycle") + medicationBase
              : treatment === "ovulation_induction"
                ? amount("oi-cycle") + medicationBase
                : amount("ivf-cycle") + medicationBase,
        status: "optional",
        multiplier: "once",
        defaultSelected: false,
        detail:
          "Adds a budget scenario for another attempt; exact cost depends on what can be reused.",
      },
    ],
  });

  return sections;
}
